/**
 * SidePanelNotificationList — full-height notification list with
 * grouping, search, timeline buckets, and IntersectionObserver
 * auto-mark-read.
 *
 * Headless: no `browser.*`, no `chrome.*`, no `theme.ts`, no storage
 * hooks. All data (notifications, conversations, appIconCache, etc.)
 * arrives as props; all transport (mark-read, dismiss, reply,
 * action) is injected as callbacks; all i18n is supplied via the
 * `labels` bag plus the per-card label bags pulled forward from
 * `NotificationListItem` / `ConversationNotificationItem`.
 *
 * The header (extension uses HeaderBar with the theme toggle, web
 * uses a simpler "Paired with X" line) is a slot — `header?: ReactNode`.
 *
 * State that stays inside the component:
 *   - search query
 *   - collapsed-apps Set (UI preference; session-scoped)
 *   - midnight tick (re-runs label memo so "Today" / "Yesterday"
 *     stay accurate without a manual refresh)
 *
 * Auto-mark-read (IntersectionObserver + dwell/flush timers) lives in the
 * `useAutoMarkRead` hook, not here.
 *
 * Why these stay headless: IntersectionObserver + setTimeout are
 * universal browser APIs (jsdom has them too); the read-marking
 * state machine is intricate but platform-free. Hosts shouldn't
 * have to re-implement it.
 */

import type { ConversationThread, DecryptedNotification } from "@reflecto/client-core";
import {
  BellOff01,
  CheckDone01,
  ChevronDown,
  ChevronRight,
  SearchLg,
  Trash01,
} from "@untitledui/icons";
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { base64ImageDataUrl } from "../../utils/format";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import {
  ConversationNotificationItem,
  type ConversationNotificationItemLabels,
} from "../ConversationNotificationItem";
import {
  type ActionResult,
  NotificationListItem,
  type NotificationListItemLabels,
  type ReplyResult,
} from "../NotificationListItem";
import { SearchBox } from "../SearchBox";
import { FooterHint, type FooterHintLabels } from "./FooterHint";
import type { DateLabels } from "./helpers";
import { findRow, getExitAnimationMs } from "./internal";
import { KbdRow } from "./KbdRow";
import styles from "./SidePanelNotificationList.module.css";
import { useAutoMarkRead } from "./useAutoMarkRead";
import { useKeyboardListNav } from "./useKeyboardListNav";
import { useNotificationListView } from "./useNotificationListView";

/**
 * i18n bag for the list-level UI (header, empty states, action
 * buttons, ARIA labels). Per-card labels live in the
 * `NotificationListItem` and `ConversationNotificationItem` bags
 * threaded through separately.
 *
 * `clearMatches` is a formatter function to keep the host's plural
 * rules out of this package — the host owns CLDR plural selection
 * (via `selectPlural`) inside the function body; mirror
 * `unreadAriaLabel` in `ConversationNotificationItemLabels`.
 *
 * `noMatchesDesc` and `appNotificationsGroup` and `dismissAllFrom`
 * take a single string arg (the search query / app name).
 */
export interface SidePanelNotificationListLabels extends DateLabels {
  searchNotifications: string;
  /** aria-label for the search box's inline clear (×) button. */
  clearSearch: string;
  clearAll: string;
  clearMatches: (count: number) => string;
  markAllRead: string;
  noNotificationsTitle: string;
  noNotificationsDesc: string;
  noMatchesTitle: string;
  noMatchesDesc: (query: string) => string;
  notificationsList: string;
  appNotificationsGroup: (appName: string) => string;
  dismissAllFrom: (appName: string) => string;
  clear: string;
  /** Visible text label for the Group/timeline view-mode checkbox in the actions toolbar. */
  groupCheckboxLabel: string;
  /**
   * PRD docs/prd-keyboard.md §4.5 footer hint. Optional — when absent
   * (or when `enableKeyboard` is false) the footer hint row isn't
   * rendered. Plain strings ("dismiss", "navigate") that follow
   * the keycap glyphs.
   *
   * A surface that renders `FooterHint` at the shell level must omit this
   * prop — passing it as well renders two footers.
   */
  keyboardHint?: FooterHintLabels;
}

export interface SidePanelNotificationListProps {
  // ── Data ───────────────────────────────────────────────────────
  notifications: DecryptedNotification[];
  conversations: ConversationThread[];
  appIconCache: Record<string, string>;
  isGrouped: boolean;
  unreadCount: number;

  // ── Actions ────────────────────────────────────────────────────
  /**
   * Called with the msgIds whose cards have dwelled in the viewport
   * past the auto-read threshold. Host marks them read in storage —
   * extension dispatches `MARK_NOTIFICATIONS_READ` to its background
   * worker; web mutates `notifications` directly via its storage
   * adapter. Batched: a single call may carry multiple msgIds.
   */
  onMarkRead: (msgIds: string[]) => void;
  /** "Mark all read" button click. */
  onMarkAllRead: () => void;
  /** Fires with the new checked value when the user toggles the Group checkbox. */
  onToggleGrouped: (checked: boolean) => void;
  onDismiss: (msgId: string) => void;
  onDismissConversation: (key: string) => void;
  /**
   * Bulk "Clear All" handler. `conversationKeys` = search-filtered
   * conversations. `msgIds` depends on whether the button is clearing
   * search matches (a search query with 1+ matches): if so, `msgIds` =
   * search-filtered regulars only — calls are excluded, since they aren't
   * part of the "N matches" the button promises. Otherwise (no search, or
   * a search with zero matches — a plain "Clear All") `msgIds` = all calls
   * + search-filtered regulars. When provided, "Clear All" calls this ONCE
   * instead of firing one `onDismiss`/`onDismissConversation` per card —
   * the host optimistically removes all of them locally and issues a
   * single batched server request. This collapses the per-card request
   * storm that, on a large backlog, tripped infra rate-limiting and left
   * cards un-dismissed ("stale left"). Omit it to keep the legacy per-card
   * loop (back-compat).
   */
  onDismissAll?: (args: { msgIds: string[]; conversationKeys: string[] }) => void;
  /**
   * Auto-mark-read for a conversation thread. Returned promise is
   * not awaited — failures are caller-logged.
   */
  onMarkConversationRead: (key: string) => Promise<void>;
  /**
   * Reply submit threaded down to per-card `NotificationListItem` /
   * `ConversationNotificationItem` instances. See those components'
   * JSDoc for the contract.
   */
  onSendReply: (msgId: string, actionIndex: number, text: string) => Promise<ReplyResult>;
  onDispatchAction: (msgId: string, actionIndex: number) => Promise<ActionResult>;
  /**
   * When true, every card withholds its reply composer and button actions. Set by a host whose
   * paired phone has no push route — those commands cannot reach it until it is next opened, and
   * a late reply is worse than none.
   *
   * Withheld silently: an earlier cut printed a per-card sentence explaining the absence, which
   * repeated on every card that had any interaction and read as an alert. The tier is already
   * reported where it belongs — the device row and bridge card — so the card just doesn't offer
   * what it cannot deliver.
   */
  interactionsUnavailable?: boolean;
  /**
   * Opens a public-API link (payload `url` or a `linkActions[].url`) when a
   * link pill is clicked. Forwarded verbatim to every `NotificationListItem`.
   * Optional — when omitted, the item falls back to `window.open`.
   */
  onOpenUrl?: (url: string) => void;

  // ── UI slots ───────────────────────────────────────────────────
  /**
   * Optional content rendered above the search/actions row. The
   * extension passes its `<HeaderBar>` (which owns the theme
   * toggle + pairing-status dot); the web passes a simpler
   * "Paired with X" line. Either may also be `undefined` for a
   * chromeless render (e.g. embedded in a parent that owns the
   * frame).
   */
  header?: ReactNode;

  /**
   * Optional content rendered below the search/actions row, at the top
   * of the list — the Glance card. Sits between the search/actions
   * row and the notification list (or empty state), so it shares the
   * list column's gutters and the notification-card vertical rhythm. It
   * renders outside the "has notifications" guard, so a recap still
   * surfaces on an empty inbox; when the slot is `undefined` (or its
   * content returns `null`) nothing is rendered — zero layout impact.
   */
  recap?: ReactNode;

  /**
   * Opt into keyboard-driven triage (PRD docs/prd-keyboard.md §5). When
   * true:
   *   - `J`/`K` move selection through every visible row (calls,
   *     conversation threads, and regular notifications) in the order
   *     they're rendered.
   *   - `E` dismisses the selected row (conversations via
   *     `onDismissConversation`, regulars via `onDismiss`).
   *   - `Esc` clears selection.
   *   - The footer hint (PRD §4.5) renders if `labels.keyboardHint`
   *     is provided.
   * Call sites that don't pass this flag get the existing pointer-
   * only experience unchanged.
   */
  enableKeyboard?: boolean;

  /**
   * Whether the host surface currently holds OS keyboard focus. Drives
   * the footer hint: `true` (default) → keycaps; `false` → the
   * "click to use keyboard" prompt. The extension side panel passes
   * `useHasFocus()`; web omits it (a normal document is focused when its
   * tab is). Only meaningful alongside `enableKeyboard` + `keyboardHint`.
   */
  keyboardFocused?: boolean;

  /**
   * Increment to programmatically focus the first unread row. The
   * extension bumps this when its service worker delivers a
   * `FOCUS_TOP_UNREAD` runtime message after the leader chord; the
   * webapp bumps it when its leader keydown listener fires. The
   * value itself doesn't matter — only that it changed since last
   * render — so callers may use an integer counter.
   *
   * **Invariant:** pass this prop from the first render with a
   * stable initial value (typically `useState(0)`). The internal
   * change-detection ref captures the value at hook-creation time so
   * the first bump always summons even if the upstream message races
   * the mount effect. Callers that add the prop mid-life (mount
   * without `focusSignal`, then add it later) WILL fire a spurious
   * summon on the prop-add — by design, to keep the race-safety.
   */
  focusSignal?: number;

  /** Un-fade signal from the host's `dismiss_rollback` runtime message. `nonce` change re-fires the effect on back-to-back rollbacks of the same id. */
  rollbackSignal?: { id: string; nonce: number };

  /** Reveal + scroll + highlight a row; bump `signal` to fire. Clears search + expands the target's collapsed group so the jump always lands (Glance `onJumpTo`; also serves the SW deep-link). */
  scrollTo?: { target: { msgId: string } | { conversationKey: string }; signal: number };

  // ── i18n ───────────────────────────────────────────────────────
  labels: SidePanelNotificationListLabels;
  /** Forwarded to every regular `NotificationListItem` rendered. */
  notificationListItemLabels: NotificationListItemLabels;
  /** Forwarded to every `ConversationNotificationItem` rendered. */
  conversationNotificationItemLabels: ConversationNotificationItemLabels;
}

export function SidePanelNotificationList({
  notifications,
  conversations,
  appIconCache,
  isGrouped,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onToggleGrouped,
  onDismiss,
  onDismissConversation,
  onDismissAll,
  onMarkConversationRead,
  onSendReply,
  onDispatchAction,
  interactionsUnavailable,
  onOpenUrl,
  header,
  recap,
  enableKeyboard = false,
  keyboardFocused = true,
  focusSignal,
  rollbackSignal,
  scrollTo,
  labels,
  notificationListItemLabels,
  conversationNotificationItemLabels,
}: SidePanelNotificationListProps) {
  const [collapsedApps, setCollapsedApps] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Clear collapsed state only on a false → true transition (explicit
  // user toggle). Ref starts undefined (not `isGrouped`) so the
  // hydration clear lands on an empty Set, never on real user collapses.
  const prevIsGroupedRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevIsGroupedRef.current === false && isGrouped === true) {
      setCollapsedApps(new Set());
    }
    prevIsGroupedRef.current = isGrouped;
  }, [isGrouped]);

  const listRef = useRef<HTMLUListElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Latest data read by the reveal effect WITHOUT making them effect deps (else reveal would
  // re-fire on every notifications/conversations change, not just on a jump).
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  // Glance jump: two-phase so the reveal (search clear + group expand) commits before we
  // resolve + scroll the row. Phase 1 reveals + stashes target + bumps jumpTick; phase 2
  // (layout effect) runs after that commit and scrolls/highlights/focuses.
  const pendingJumpRef = useRef<{ msgId: string } | { conversationKey: string } | null>(null);
  const [jumpTick, setJumpTick] = useState(0);
  const jumpHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightedRowRef = useRef<HTMLElement | null>(null);

  // Phase 1 — reveal. Fires on each scrollTo identity change (host bumps `signal` → new object).
  useEffect(() => {
    if (!scrollTo) return;
    const { target } = scrollTo;
    pendingJumpRef.current = target;
    setSearchQuery("");
    const pkg =
      "msgId" in target
        ? notificationsRef.current.find((n) => n.msgId === target.msgId)?.appPackage
        : conversationsRef.current.find((c) => c.key === target.conversationKey)?.packageName;
    if (pkg) {
      setCollapsedApps((prev) => {
        if (!prev.has(pkg)) return prev;
        const next = new Set(prev);
        next.delete(pkg);
        return next;
      });
    }
    setJumpTick((t) => t + 1);
  }, [scrollTo]);

  // Phase 2 — scroll + highlight + focus, after the reveal render commits.
  useLayoutEffect(() => {
    if (jumpTick === 0) return;
    const target = pendingJumpRef.current;
    pendingJumpRef.current = null;
    if (!target) return;
    const list = listRef.current;
    if (!list) return;
    const id = "msgId" in target ? target.msgId : target.conversationKey;
    const row = findRow(list, id);
    if (!row) return; // best-effort: target not in the rendered set
    row.scrollIntoView({ block: "nearest", behavior: "instant" });
    row.focus({ preventScroll: true }); // best-effort a11y; no-op if not focusable
    // Clear a prior highlight (re-jump within the 2s window) before highlighting the new row.
    if (jumpHighlightTimerRef.current) clearTimeout(jumpHighlightTimerRef.current);
    highlightedRowRef.current?.classList.remove(styles.jumpHighlight);
    row.classList.add(styles.jumpHighlight);
    highlightedRowRef.current = row;
    jumpHighlightTimerRef.current = setTimeout(() => {
      row.classList.remove(styles.jumpHighlight);
      highlightedRowRef.current = null;
      jumpHighlightTimerRef.current = null;
    }, 2000);
  }, [jumpTick]);

  // Clear a pending highlight timer + any lingering class on unmount.
  useEffect(() => {
    return () => {
      if (jumpHighlightTimerRef.current) clearTimeout(jumpHighlightTimerRef.current);
      highlightedRowRef.current?.classList.remove(styles.jumpHighlight);
    };
  }, []);

  // IntersectionObserver-driven auto-mark-read for both regular
  // notification cards (1500ms dwell + 300ms batched flush) and
  // conversation threads (1500ms dwell, per-thread fire-and-forget).
  // The scroll container (`scrollRef` — the recap card + list scroll
  // together) is the observer root, so dwell is measured against the
  // actual viewport. See `useAutoMarkRead` for the full state-machine spec.
  useAutoMarkRead({
    listRef,
    scrollRef,
    notifications,
    conversations,
    onMarkRead,
    onMarkConversationRead,
  });

  // Dismiss exit animation: flip the visual flag now (instant feedback),
  // forward to the host after `getExitAnimationMs()` so the user sees the
  // motion before the data is dropped. Host callbacks live in refs to keep
  // the interceptors' identity stable across parent re-renders.
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(() => new Set());
  // Per-id timer + host-forward closure. Unmount calls `dispatch` directly to avoid setState-into-unmounted.
  const dismissTimersRef = useRef<
    Map<string, { timer: ReturnType<typeof setTimeout>; dispatch: () => void }>
  >(new Map());
  // Per-id focus-restoration target captured at blur-time; restored on rollback.
  const blurredElementsRef = useRef<Map<string, HTMLElement | null>>(new Map());
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const onDismissConversationRef = useRef(onDismissConversation);
  onDismissConversationRef.current = onDismissConversation;
  // Up-front so startDismiss can scope its blur without forward-referencing the hook's result.
  const columnRootRef = useRef<HTMLDivElement>(null);

  const startDismiss = useCallback((id: string, dispatch: () => void) => {
    // Idempotent — second click / second E during the animation is a no-op.
    if (dismissTimersRef.current.has(id)) return;
    // Scope-blur for WCAG 2.1 SC 2.4.3: drop focus before the wrapper goes aria-hidden,
    // but only if it lives in this column — don't steal focus from a parent search input.
    const active = document.activeElement;
    if (active instanceof HTMLElement && columnRootRef.current?.contains(active)) {
      blurredElementsRef.current.set(id, active);
      active.blur();
    } else {
      blurredElementsRef.current.set(id, null);
    }
    setDismissingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    // Delete the map entry inside the timer body BEFORE dispatch() so the
    // unmount cleanup never invokes a timer that already fired (which would
    // double-dispatch and trigger an unnecessary rollback round-trip).
    const timer = setTimeout(() => {
      dismissTimersRef.current.delete(id);
      blurredElementsRef.current.delete(id);
      dispatch();
    }, getExitAnimationMs());
    dismissTimersRef.current.set(id, { timer, dispatch });
  }, []);

  const handleDismiss = useCallback(
    (msgId: string) => {
      startDismiss(msgId, () => onDismissRef.current(msgId));
    },
    [startDismiss]
  );

  const handleDismissConversation = useCallback(
    (key: string) => {
      startDismiss(key, () => onDismissConversationRef.current(key));
    },
    [startDismiss]
  );

  // Unmount: fire pending dispatches so a mid-animation dismiss isn't lost.
  useEffect(() => {
    return () => {
      const pending = Array.from(dismissTimersRef.current.values());
      dismissTimersRef.current.clear();
      blurredElementsRef.current.clear();
      for (const entry of pending) {
        clearTimeout(entry.timer);
        try {
          entry.dispatch();
        } catch {
          // Service worker may have torn down; sendMessage can throw.
        }
      }
    };
  }, []);

  // Host signals `rollbackSignal` when the background's queue-splice
  // rollback path (Case 1) fires — the card never leaves the data, so the
  // lazy cleanup effect won't clear dismissingIds on its own. `nonce`
  // re-fires this effect for back-to-back rollbacks of the same id.
  useEffect(() => {
    if (!rollbackSignal) return;
    const { id } = rollbackSignal;
    const entry = dismissTimersRef.current.get(id);
    if (entry) {
      clearTimeout(entry.timer);
      dismissTimersRef.current.delete(id);
    }
    const el = blurredElementsRef.current.get(id);
    blurredElementsRef.current.delete(id);
    setDismissingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    // Focus after React commits the dismissingIds removal — otherwise the
    // KbdRow wrapper is still aria-hidden for one frame (WCAG 2.1 SC 2.4.3).
    if (el?.isConnected && columnRootRef.current?.contains(el)) {
      requestAnimationFrame(() => {
        if (el.isConnected && columnRootRef.current?.contains(el)) {
          el.focus({ preventScroll: true });
        }
      });
    }
  }, [rollbackSignal]);

  function toggleApp(app: string) {
    setCollapsedApps((prev) => {
      const next = new Set(prev);
      if (next.has(app)) {
        next.delete(app);
      } else {
        next.add(app);
      }
      return next;
    });
  }

  function dismissAllFromApp(
    appPackage: string,
    items: DecryptedNotification[],
    convKeys: string[]
  ) {
    const appCallNotifications = callNotifications.filter((n) => n.appPackage === appPackage);
    const msgIds = [...appCallNotifications, ...items].map((n) => n.msgId);
    if (onDismissAll) {
      onDismissAll({ msgIds, conversationKeys: convKeys });
      return;
    }
    for (const n of appCallNotifications) {
      handleDismiss(n.msgId);
    }
    for (const n of items) {
      handleDismiss(n.msgId);
    }
    for (const key of convKeys) {
      handleDismissConversation(key);
    }
  }

  // Pure view derivations: filter by search, split calls vs regulars,
  // sort by time, group by app, bucket by date. Also owns the midnight
  // tick that re-runs the date-label memo so "Today"/"Yesterday" stay
  // accurate without a refresh. See `useNotificationListView`.
  const {
    callNotifications,
    regularNotifications,
    filteredRegular,
    filteredConversations,
    sorted,
    appGroups,
    timelineBuckets,
    allGroupKeys,
    matchCount,
  } = useNotificationListView({
    notifications,
    conversations,
    isGrouped,
    searchQuery,
    todayLabel: labels.today,
    yesterdayLabel: labels.yesterday,
  });

  // Mirror via ref so the cleanup effect doesn't re-fire on each
  // `setDismissingIds` (new Set reference every call → redundant pass).
  const dismissingIdsRef = useRef(dismissingIds);
  dismissingIdsRef.current = dismissingIds;

  // Lazy cleanup: when a card leaves the data after the host dispatch,
  // drop its id from `dismissingIds`. Running here (not in the timer
  // callback) avoids the ~50ms opacity-rebound flash between dispatch and
  // the background's storage flush.
  useEffect(() => {
    const current = dismissingIdsRef.current;
    if (current.size === 0) return;
    // Use raw props, not filtered views — a search query mid-animation must not cancel a pending timer.
    const liveIds = new Set([
      ...notifications.map((n) => n.msgId),
      ...conversations.map((c) => c.key),
    ]);
    const stale = [...current].filter((id) => !liveIds.has(id));
    if (stale.length === 0) return;
    // Cancel the scheduled timer before deleting — without clearTimeout the
    // timer fires later and double-dispatches (ghost dismiss on every
    // success path, restore/dismiss flicker on rollback Case 2). Mutation
    // runs outside the state updater so Strict Mode double-invocations
    // don't repeat the side effects.
    for (const id of stale) {
      const entry = dismissTimersRef.current.get(id);
      // dispatch deliberately omitted: the card left the data without
      // our timer firing, which means SSE (or another client) already
      // cleared it. Re-dispatching would issue a duplicate server DELETE.
      if (entry) clearTimeout(entry.timer);
      dismissTimersRef.current.delete(id);
      blurredElementsRef.current.delete(id);
    }
    setDismissingIds((prev) => {
      const next = new Set(prev);
      for (const id of stale) {
        next.delete(id);
      }
      return next;
    });
  }, [notifications, conversations]);

  const isClearingSearchMatches = Boolean(searchQuery) && matchCount > 0;

  function dismissAll() {
    // `callNotifications` is intentionally NOT search-filtered and are not
    // counted in the "Clear N matches" label, so keep search-scoped clears to
    // the exact result set the button promises.
    if (onDismissAll) {
      // One batched call. The host owns the optimistic removal + single server
      // request, so a large backlog can't fan out into a per-card request storm.
      const msgIds = [
        ...(isClearingSearchMatches ? [] : callNotifications.map((n) => n.msgId)),
        ...filteredRegular.map((n) => n.msgId),
      ];
      const conversationKeys = filteredConversations.map((conv) => conv.key);
      onDismissAll({ msgIds, conversationKeys });
      return;
    }
    if (!isClearingSearchMatches) {
      for (const n of callNotifications) {
        handleDismiss(n.msgId);
      }
    }
    for (const n of filteredRegular) {
      handleDismiss(n.msgId);
    }
    for (const conv of filteredConversations) {
      handleDismissConversation(conv.key);
    }
  }
  // Guard `matchCount === 0` so `clearMatches(0)` is never computed —
  // the Clear button is also gated on `filteredRegular.length > 0 ||
  // filteredConversations.length > 0` below, but evaluating the label
  // unconditionally would still call `clearMatches(0)` on every render
  // when a search has no results, which is wasted work and a latent bug
  // if the gating condition ever shifts. Plural selection lives inside
  // `clearMatches` (the host's `selectPlural`), so the call is
  // unconditional here regardless of count.
  const clearAllLabel = isClearingSearchMatches ? labels.clearMatches(matchCount) : labels.clearAll;

  const noNotificationsEmptyState = (
    <div className={styles.empty}>
      <BellOff01 size={48} className={styles.emptyIcon} aria-hidden="true" />
      <p className={styles.emptyTitle}>{labels.noNotificationsTitle}</p>
      <p className={styles.emptyDescription}>{labels.noNotificationsDesc}</p>
    </div>
  );

  // ── Keyboard navigation (PRD docs/prd-keyboard.md §5) ───────────────
  // Selection order, useKeyboardNavigation wiring, focusSignal effect,
  // and scrollIntoView effect all live in useKeyboardListNav. The
  // parent passes `columnRootRef` in (declared above for startDismiss)
  // and consumes `selectedId` (for per-row aria-current / tabIndex).
  const { selectedId } = useKeyboardListNav({
    enabled: enableKeyboard,
    focusSignal,
    listRef,
    notifications,
    conversations,
    callNotifications,
    isGrouped,
    appGroups,
    allGroupKeys,
    collapsedApps,
    filteredConversations,
    timelineBuckets,
    // Wrapped versions of the host callbacks: each routes through
    // `startDismiss` so the exit animation runs before forwarding to the
    // host. The hook routes E to one of these based on row kind.
    onDismiss: handleDismiss,
    onDismissConversation: handleDismissConversation,
    columnRootRef,
  });

  return (
    <div
      ref={columnRootRef}
      tabIndex={enableKeyboard ? -1 : undefined}
      className={enableKeyboard ? `${styles.container} ${styles.kbdRoot}` : styles.container}
    >
      {header}

      {(regularNotifications.length > 0 ||
        callNotifications.length > 0 ||
        conversations.length > 0) && (
        <>
          <div className={styles.searchRow}>
            <SearchBox
              label={labels.searchNotifications}
              clearLabel={labels.clearSearch}
              placeholder={labels.searchNotifications}
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery("")}
            />
          </div>
          <div className={styles.actionsRow}>
            <Checkbox
              checked={isGrouped}
              onChange={onToggleGrouped}
              label={labels.groupCheckboxLabel}
            />
            <div className={styles.actionsRight}>
              {unreadCount > 0 && (
                <Button variant="ghost" size="xs" leadingIcon={CheckDone01} onClick={onMarkAllRead}>
                  {labels.markAllRead}
                </Button>
              )}
              {(callNotifications.length > 0 ||
                filteredRegular.length > 0 ||
                filteredConversations.length > 0) && (
                <Button variant="ghost" size="xs" leadingIcon={Trash01} onClick={dismissAll}>
                  {clearAllLabel}
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      {/* The single scroll area: the recap card and the list (or empty
              state) scroll together here while the header + search/actions stay
              pinned above. Owns the list overflow; the auto-mark-read observer
              uses this element as its root (see the `scrollRef` passed to
              `useAutoMarkRead`). */}
      <div ref={scrollRef} className={styles.scrollArea}>
        {/* recap card slot — below the search/actions, above the
                list/empty-state. Outside the content guard so it surfaces on an
                empty inbox. The wrapper supplies the 16px side gutter + 8px bottom
                gap so the card's edges align with the notification cards' inset and
                inter-card rhythm. Zero layout impact when the card returns null: the gap
                rides on `.recapSlot > *` (no child = no gap), since a React null renders a
                comment node that defeats `:empty`. */}
        {recap && <div className={styles.recapSlot}>{recap}</div>}

        {callNotifications.length === 0 &&
          regularNotifications.length === 0 &&
          conversations.length === 0 &&
          noNotificationsEmptyState}

        {(regularNotifications.length > 0 || conversations.length > 0) &&
          searchQuery &&
          filteredRegular.length === 0 &&
          filteredConversations.length === 0 && (
            <div className={styles.empty}>
              <SearchLg size={48} className={styles.emptyIcon} aria-hidden="true" />
              <p className={styles.emptyTitle}>{labels.noMatchesTitle}</p>
              <p className={styles.emptyDescription}>{labels.noMatchesDesc(searchQuery)}</p>
            </div>
          )}

        {callNotifications.length > 0 ||
        allGroupKeys.length > 0 ||
        (sorted && sorted.length > 0) ||
        filteredConversations.length > 0 ? (
          <ul ref={listRef} className={styles.list} aria-label={labels.notificationsList}>
            {/* Calls render first and stay visible regardless of search.
                  `KbdRow` with `as="li"` always renders the `<li>` (list
                  semantics require it) but conditionally adds the
                  roving-tabindex / aria-current attrs. The ARIA rationale
                  (no `role="option"` because the card has focusable
                  children, `aria-current` over `aria-selected`) is
                  documented on the `KbdRow` helper itself. */}
            {callNotifications.map((n) => {
              const isDismissing = dismissingIds.has(n.msgId);
              return (
                <KbdRow
                  key={n.msgId}
                  id={n.msgId}
                  enabled={enableKeyboard}
                  selected={enableKeyboard && selectedId === n.msgId}
                  dismissing={isDismissing}
                  as="li"
                >
                  <NotificationListItem
                    notification={n}
                    appIconUrl={appIconCache[n.appPackage]}
                    onDismiss={handleDismiss}
                    onSendReply={onSendReply}
                    onDispatchAction={onDispatchAction}
                    interactionsUnavailable={interactionsUnavailable}
                    onOpenUrl={onOpenUrl}
                    dismissing={isDismissing}
                    labels={notificationListItemLabels}
                  />
                </KbdRow>
              );
            })}
            {isGrouped
              ? allGroupKeys.map((appPackage) => {
                  const group = appGroups?.get(appPackage);
                  const groupConvs = filteredConversations.filter(
                    (c) => c.packageName === appPackage
                  );
                  const appDisplayName = group?.app ?? groupConvs[0]?.app ?? appPackage;
                  const totalCount = (group?.items.length ?? 0) + groupConvs.length;

                  return (
                    // Roving-tabindex pattern (the listbox/option chain
                    // was dropped because the rows wrap interactive
                    // descendants — see the ARIA rationale above and on `KbdRow`).
                    // `<li>` is its native list-item, no extra ARIA role
                    // is needed; `aria-label` would only matter inside
                    // a composite widget.
                    <li key={appPackage} className={styles.group}>
                      {/* biome-ignore lint/a11y/useSemanticElements: contains nested button, cannot use <button> */}
                      <div
                        className={styles.groupHeader}
                        onClick={() => toggleApp(appPackage)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={!collapsedApps.has(appPackage)}
                        aria-label={labels.appNotificationsGroup(appDisplayName)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleApp(appPackage);
                          }
                        }}
                      >
                        <span className={styles.groupName}>
                          {appIconCache[appPackage] && (
                            <img
                              src={base64ImageDataUrl(appIconCache[appPackage])}
                              alt=""
                              className={styles.groupIcon}
                              width={20}
                              height={20}
                            />
                          )}
                          {appDisplayName}
                          <span className={styles.groupCount}>{totalCount}</span>
                        </span>
                        <div className={styles.groupActions}>
                          <button
                            type="button"
                            className={styles.dismissAllBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissAllFromApp(
                                appPackage,
                                group?.items ?? [],
                                groupConvs.map((conv) => conv.key)
                              );
                            }}
                            title={labels.dismissAllFrom(appDisplayName)}
                          >
                            {labels.clear}
                          </button>
                          <span className={styles.chevron}>
                            {collapsedApps.has(appPackage) ? (
                              <ChevronRight size={14} aria-hidden="true" />
                            ) : (
                              <ChevronDown size={14} aria-hidden="true" />
                            )}
                          </span>
                        </div>
                      </div>

                      {!collapsedApps.has(appPackage) && (
                        <>
                          {groupConvs.map((conv) => {
                            const isDismissing = dismissingIds.has(conv.key);
                            return (
                              <KbdRow
                                key={conv.key}
                                id={conv.key}
                                enabled={enableKeyboard}
                                selected={enableKeyboard && selectedId === conv.key}
                                dismissing={isDismissing}
                              >
                                <ConversationNotificationItem
                                  thread={conv}
                                  appIcon={appIconCache[conv.packageName]}
                                  onDismiss={handleDismissConversation}
                                  onSendReply={onSendReply}
                                  interactionsUnavailable={interactionsUnavailable}
                                  dismissing={isDismissing}
                                  labels={conversationNotificationItemLabels}
                                  indent
                                />
                              </KbdRow>
                            );
                          })}
                          {group?.items.map((n) => {
                            const isDismissing = dismissingIds.has(n.msgId);
                            return (
                              <KbdRow
                                key={n.msgId}
                                id={n.msgId}
                                enabled={enableKeyboard}
                                selected={enableKeyboard && selectedId === n.msgId}
                                dismissing={isDismissing}
                              >
                                <NotificationListItem
                                  notification={n}
                                  showAppIcon={false}
                                  indent
                                  appIconUrl={appIconCache[n.appPackage]}
                                  onDismiss={handleDismiss}
                                  onSendReply={onSendReply}
                                  onDispatchAction={onDispatchAction}
                                  interactionsUnavailable={interactionsUnavailable}
                                  onOpenUrl={onOpenUrl}
                                  dismissing={isDismissing}
                                  labels={notificationListItemLabels}
                                />
                              </KbdRow>
                            );
                          })}
                        </>
                      )}
                    </li>
                  );
                })
              : timelineBuckets.map((bucket) => {
                  // React key derives from the first item's stable id rather
                  // than `bucket.label` — `getDateLabel` runs locale-formatted
                  // dates through `toLocaleDateString` for older buckets, and
                  // `Today`/`Yesterday` are i18n-overridable, so label-only keys
                  // could collide across locales (e.g. two locales rendering
                  // the same short month name). Buckets are non-empty by
                  // construction in `buildTimelineBuckets`, so `items[0]` is
                  // always defined.
                  const first = bucket.items[0];
                  const bucketKey =
                    first.kind === "conversation" ? first.data.key : first.data.msgId;
                  return (
                    // No `aria-label` on the `<li>` — ARIA 1.2 ignores
                    // it on plain listitems, and the `<span class=
                    // "dateLabel">` inside `dateSeparator` already
                    // announces the bucket boundary in document order.
                    <li key={bucketKey}>
                      {timelineBuckets.length > 1 && (
                        <div className={styles.dateSeparator}>
                          <span className={styles.dateLabel}>{bucket.label}</span>
                        </div>
                      )}
                      {bucket.items.map((item) => {
                        const id = item.kind === "conversation" ? item.data.key : item.data.msgId;
                        const isDismissing = dismissingIds.has(id);
                        return (
                          <KbdRow
                            key={id}
                            id={id}
                            enabled={enableKeyboard}
                            selected={enableKeyboard && selectedId === id}
                            dismissing={isDismissing}
                          >
                            {item.kind === "conversation" ? (
                              <ConversationNotificationItem
                                thread={item.data}
                                appIcon={appIconCache[item.data.packageName]}
                                onDismiss={handleDismissConversation}
                                onSendReply={onSendReply}
                                interactionsUnavailable={interactionsUnavailable}
                                dismissing={isDismissing}
                                labels={conversationNotificationItemLabels}
                              />
                            ) : (
                              <NotificationListItem
                                notification={item.data}
                                appIconUrl={appIconCache[item.data.appPackage]}
                                onDismiss={handleDismiss}
                                onSendReply={onSendReply}
                                onDispatchAction={onDispatchAction}
                                interactionsUnavailable={interactionsUnavailable}
                                onOpenUrl={onOpenUrl}
                                dismissing={isDismissing}
                                labels={notificationListItemLabels}
                              />
                            )}
                          </KbdRow>
                        );
                      })}
                    </li>
                  );
                })}
          </ul>
        ) : null}
      </div>

      {enableKeyboard && labels.keyboardHint && (
        <FooterHint labels={labels.keyboardHint} focused={keyboardFocused} />
      )}
    </div>
  );
}

export type { FooterHintLabels } from "./FooterHint";
export { FooterHint } from "./FooterHint";
// Re-export helpers + types for tests + downstream consumers.
export {
  type AppGroup,
  buildTimelineBuckets,
  type DateLabels,
  getDateLabel,
  groupByApp,
  type TimelineBucket,
  type TimelineItem,
} from "./helpers";
